// src/themes/modern/menu/ModernMenuPage.tsx
import { motion } from "framer-motion";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaUtensils } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useBranch } from "~/contexts/BranchContext";
import { type MenuItem } from "~/types/new_types";
import CategoriesComponent from "./components/CategoriesComponent";
import FeaturedMenuSection from "./components/FeaturedMenuSection";
import MenuItemDetailsModal from "./components/MenuItemDetailsModal";
import FeedbackModal from "~/components/feedback/FeedbackModal";
import { BiSolidCategory } from "react-icons/bi";
import AnnouncementCarousel from "~/components/announcements/AnnouncementCarousel";

type Props = {};

const ModernMenuPage: React.FC<Props> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedItem, setSelectedItem } = useBranch();
  const [feedbackItem, setFeedbackItem] = useState<MenuItem | null>(null);

  const { branchData, theme } = useBranch();

  if (!branchData) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center"
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-red-600 text-6xl mb-4"
          >
            <FaUtensils />
          </motion.div>
          <h1 className="text-2xl font-bold mb-2">
            {t("restaurant.notFound.title")}
          </h1>
          <p className="text-gray-600 mb-6">
            {t("restaurant.notFound.message")}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: branchData.branch.theme.backgroundColor,
        color: branchData.branch.theme.textColor,
        fontFamily: branchData.branch.theme.fontFamily,
      }}
    >
      <MenuItemDetailsModal
        item={selectedItem!}
        show={!!selectedItem}
        close={() => setSelectedItem(null)}
        theme={branchData.branch.theme}
        onFeedback={(item) => setFeedbackItem(item)}
      />

      {/* Announcements Carousel */}
      <AnnouncementCarousel />

      {/* Featured Items Carousel */}
      <FeaturedMenuSection
        setSelectedItem={setSelectedItem}
      />

      <section className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <h2
            className={`${theme?.titleFontSize} ${theme?.boldTitles ? "font-bold" : ""
              } flex items-center gap-2`}
            style={{ color: theme?.textColor }}
          >
            <BiSolidCategory
              className="text-xl"
              style={{ color: theme?.primaryColor }}
            />
            <span>{t("categories.ourCategories")}</span>
          </h2>
          <p style={{ color: `${theme?.textColor}80` }}>
            {t("categories.categoriesDescription")}
          </p>
        </motion.div>

        <CategoriesComponent
          layout="grid"
          onCategorySelect={(categoryId) => {
            navigate(categoryId === "menu" ? "." : categoryId);
          }}
        />
      </section>

      {/* Feedback Modal */}
      <FeedbackModal
        show={!!feedbackItem}
        onClose={() => setFeedbackItem(null)}
        item={feedbackItem || undefined}
      />
    </div>
  );
};

export default ModernMenuPage;
